import { Component, Event, EventEmitter, JSX, Method, Prop, State, Watch, h } from '@stencil/core'
import Engine from '@briza/illogical'
import MarkdownIt from 'markdown-it'

import {
  AnswerMap,
  LayoutSection,
  QuestionnaireEventDetail,
  SectionItem,
} from './air-questionnaire.types'
import {
  AnswerValue,
  NoneSelected,
  Question,
  QuestionAnswer,
  QuestionType,
  QuestionUIControlType,
  QuestionValidationRuleType,
} from '../air-question/air-question.types'
import { Address } from '../air-address/air-address.types'
import { BusinessClass } from '../air-business-class/air-business-class.types'
import { QuestionGroupChange } from '../air-question-group/air-question-group.types'
import { Status } from '../../types/status.types'
import { WorkersCompensationClass } from '../air-workers-compensation-class/air-workers-compensation-class.types'
import { isMobile } from '../../utils/matchMedia'
import { validateQuestion } from '../air-question/validation'
import { windowProvider } from '../../utils/windowProvider'

const engine = new Engine()
const markdownIt = new MarkdownIt()

@Component({
  tag: 'air-questionnaire',
  styleUrl: 'air-questionnaire.scss',
  shadow: true,
})
export class AirQuestionnaire {
  /** Questions */
  @Prop() questions!: Question[]

  /** Sections */
  @Prop() sections!: LayoutSection[]

  /** Answers (optional) */
  @Prop({ mutable: true }) answers: AnswerMap = {}
  @Watch('answers')
  watchAnswers(answers: AnswerMap): void {
    this.visibleQuestions = this.getVisibleQuestions(answers)
    const answersFromVisibleQuestions = this.getAnswersForVisibleQuestions(answers)

    const valid = Object.entries(answersFromVisibleQuestions).every(([questionId, answer]) => {
      return (
        validateQuestion(this.getQuestionById(questionId), answers, answer).status ===
        Status.Success
      )
    })

    const completed = this.visibleQuestions.every((question) => {
      return !question.required || answers[question.id] !== undefined
    })

    if (valid === true) {
      this.update.emit({
        answers,
        completed,
      })
    }
  }

  /** BusinessClasses (required if your application has "ClassOfBusiness" question) */
  @Prop() businessClasses: BusinessClass[] = []

  /** Workers Compensation dictionary with states slug as keys (required if your application has "WcClassCode" question) */
  @Prop() workersCompensationClassesByState: Record<string, WorkersCompensationClass[]> = {}

  /** Display or not questions that already had answers during initialization */
  @Prop() skipPrefilledQuestions = false

  @State() openedInfoBoxId: string | null = null

  @State() questionGroupCounters: Record<string, number> = {}

  /** Triggers when an answer has changed or validation is triggered */
  @Event({ eventName: 'air-questionnaire-update' }) update!: EventEmitter<QuestionnaireEventDetail>

  @State() isMobile = false

  /** Validate visible questions */
  @Method()
  async validate(): Promise<void> {
    this.validateAllVisibleQuestions = true
    this.resetVisibleQuestionsAndAnswers(this.answers)
  }

  private visibleQuestions: Question[] = []
  private prefilledQuestions: Set<string> = new Set()
  private validateAllVisibleQuestions = false

  componentWillLoad(): void {
    // On start, store prefilled answers to avoid rendering them
    if (this.skipPrefilledQuestions) {
      this.questions.forEach((question) => {
        if (this.answers[question.id] !== undefined) {
          this.prefilledQuestions.add(question.id)
        }
      })
    }

    if (!this.isQuestionnaireValid()) return
    this.resetVisibleQuestionsAndAnswers(this.answers)

    windowProvider().addEventListener('resize', this.handleResize)
    this.handleResize()
  }

  disconnectedCallback(): void {
    windowProvider().removeEventListener('resize', this.handleResize)
  }

  private handleResize = (): void => {
    this.isMobile = isMobile()
  }

  private getVisibleQuestions(answers: AnswerMap): Question[] {
    return this.questions.filter((question) => {
      return question.condition ? engine.evaluate(question.condition, answers) : true
    })
  }

  private resetVisibleQuestionsAndAnswers(answers: AnswerMap): void {
    this.visibleQuestions = this.getVisibleQuestions(answers)
    this.answers = this.getAnswersForVisibleQuestions(answers)
  }

  private getAnswersForVisibleQuestions(answers: AnswerMap): AnswerMap {
    return Object.fromEntries(
      this.visibleQuestions
        .map((question): [string, AnswerValue] => [
          question.id,
          answers[question.id] === undefined && question.default !== undefined
            ? question.default
            : answers[question.id],
        ])
        .filter(([, answer]) => answer !== undefined),
    )
  }

  private getSectionClasses(sectionOrGroup: string, depth: number): Record<string, boolean> {
    return {
      [`${sectionOrGroup}`]: true,
      [`depth-${depth}`]: true,
    }
  }

  private renderSectionTitle(title: string, depth: number): JSX.Element {
    return (
      <h2 class="title" innerHTML={markdownIt.render(title)}>
        {depth === 1 && <air-divider variant="dark"></air-divider>}
      </h2>
    )
  }

  private renderSectionDescription(description: string): JSX.Element {
    return <div class="description" innerHTML={markdownIt.render(description)}></div>
  }

  private renderQuestionGroup(items: SectionItem[], depth = 0): JSX.Element | null {
    //get the id for counting from the question ID
    const getCounterId = (sectionItem: SectionItem[]): string => {
      return typeof sectionItem[0] === 'string'
        ? sectionItem[0]
        : getCounterId(sectionItem[0].items)
    }

    const counterId = getCounterId(items)
    const visibleCount = this.questionGroupCounters[counterId] || 1
    return (
      <air-question-group
        questionIdToChange={counterId}
        visibleCount={visibleCount}
        max={items.length}
        onAir-group-control-change={this.handleQuestionGroupChange}
        isSecondary={depth > 1}
      >
        {items
          .map((item) =>
            typeof item === 'string'
              ? this.renderQuestion(this.getQuestionById(item))
              : this.renderSections([item], depth + 1),
          )
          .splice(0, visibleCount)}
      </air-question-group>
    )
  }

  private renderQuestion(question: Question): JSX.Element {
    const addressNumber = question.id.match(/\d/g)?.[0]

    // filter classOfBusiness data
    if (question.type === QuestionType.ClassOfBusiness) {
      if (this.businessClasses.some((item) => 'children' in item)) {
        throw new Error(`Received deprecated business classes data for question ${question.id}`)
      }
    }

    // pass workersCompensationClasses data to WcClassCode question
    let workersCompensationClasses: WorkersCompensationClass[] = []
    if (question.type === QuestionType.WcClassCode) {
      const locationAddressAnswer = this.answers[`Location${addressNumber}Address`] as Address
      const addressState = locationAddressAnswer?.region
      if (addressState !== undefined) {
        workersCompensationClasses = this.workersCompensationClassesByState[addressState]
      }
    }

    const hasPresentValidationRule = question.validation?.some(
      (rule) => rule.type === QuestionValidationRuleType.Present,
    )

    if (
      question.type === QuestionType.MultiSelect &&
      question.options &&
      !question.options.some((option) => option.key === NoneSelected) &&
      !hasPresentValidationRule &&
      question.required
    ) {
      if (question.ui.control === QuestionUIControlType.Dropdown) {
        question.options = [
          {
            key: NoneSelected,
            label: 'None apply',
          },
          ...question.options,
        ]
      } else {
        question.options?.push({
          key: NoneSelected,
          label: 'None of the above',
        })
      }
    }

    const answer =
      this.answers[question.id] === undefined && question.default !== undefined
        ? question.default
        : this.answers[question.id]

    const validation =
      answer !== undefined || this.validateAllVisibleQuestions
        ? validateQuestion(question, this.answers, answer)
        : undefined
    return (
      <div class="question">
        <air-question
          key={question.id}
          onAir-question-focus={this.handleFocus}
          onAir-question-blur={this.handleAnswer}
          onAir-question-change={this.handleAnswer}
          onAir-info={this.handleInfo}
          prefer-question-within-group={question.questionWithinGroup !== undefined}
          question={question}
          validation={validation}
          answer={answer}
          businessClasses={this.businessClasses}
          workersCompensationClasses={workersCompensationClasses}
        ></air-question>
        {this.renderInfoBox(question)}
      </div>
    )
  }

  private getQuestionById(questionId: string): Question {
    const question = this.questions.find((question) => question.id === questionId)
    if (question === undefined) {
      throw new Error(`question ${questionId} not found`)
    }
    return question
  }

  private renderInfoBox(question: Question): JSX.Element {
    const hint = question.ui?.hint
    const opened = this.openedInfoBoxId === question.id

    return (
      hint !== undefined &&
      opened === true && (
        <div class="question-info">
          <air-info-box
            dismissible
            text={hint[0]}
            markdownData={hint.slice(1)}
            is-modal={this.isMobile}
            opened={opened}
            onAir-close={this.handleInfoClose}
          ></air-info-box>
        </div>
      )
    )
  }

  private handleInfo = (event: CustomEvent<Question>): void => {
    this.openedInfoBoxId = event.detail.id
  }

  private handleInfoClose = (): void => {
    this.openedInfoBoxId = null
  }

  private handleFocus = (event: CustomEvent<QuestionAnswer>): void => {
    if (isMobile() === false) {
      this.openedInfoBoxId = event.detail.question.id
    }
  }

  private handleQuestionGroupChange = (event: CustomEvent<QuestionGroupChange>): void => {
    if (/Location\d+Address/.test(event.detail.questionId)) {
      this.resetVisibleQuestionsAndAnswers({
        ...this.answers,
        NumberOfLocations: event.detail.visibleCount,
      })
    }

    if (/Location\d+Wc\d+Code/.test(event.detail.questionId)) {
      const groupQuestionsIds = Object.keys(this.answers).filter((item) =>
        /Location\d+Wc\d+Code/.test(item),
      )
      const questionsIdsToRemove = groupQuestionsIds.filter(
        (questionId) => groupQuestionsIds.indexOf(questionId) > event.detail.visibleCount - 1,
      )
      this.resetVisibleQuestionsAndAnswers(
        Object.keys(this.answers)
          .filter((questionId) => !questionsIdsToRemove.includes(questionId))
          .reduce((answers: AnswerMap, questionId: string) => {
            answers[questionId] = this.answers[questionId]
            return answers
          }, {}),
      )
    }
    this.questionGroupCounters = {
      ...this.questionGroupCounters,
      [event.detail.questionId]: event.detail.visibleCount,
    }
  }

  private handleAnswer = (event: CustomEvent<QuestionAnswer>): void => {
    this.resetVisibleQuestionsAndAnswers({
      ...this.answers,
      [event.detail.question.id]: event.detail.answer,
    })
  }

  private isQuestionnaireValid(): boolean {
    return (
      Array.isArray(this.questions) &&
      this.questions.length > 0 &&
      Array.isArray(this.sections) &&
      this.sections.length > 0
    )
  }

  private renderSections(sections: LayoutSection[], depth = 0): JSX.Element[] {
    return sections.map((section: LayoutSection) => {
      const items = section.items.reduce<JSX.Element[]>((acc, item) => {
        if (typeof item === 'string') {
          const isVisible = this.visibleQuestions.some((question) => item === question.id)
          const notHidden = !['NumberOfLocations'].includes(item)
          const notPrefilled =
            !this.skipPrefilledQuestions ||
            !this.prefilledQuestions.has(this.getQuestionById(item).id)

          const shouldRender = isVisible && notHidden && notPrefilled

          return shouldRender ? [...acc, this.renderQuestion(this.getQuestionById(item))] : acc
        }

        const renderedSections = this.renderSections([item], depth + 1).filter(
          (section) => section !== undefined && section !== false,
        )

        return renderedSections.length > 0 ? [...acc, renderedSections] : acc
      }, [])

      const useQuestionGroup =
        section.items.every(
          (item) => typeof item === 'string' && /Location\d+Wc\d+Code/.test(item),
        ) ||
        section.items.some(
          (item) =>
            typeof item !== 'string' &&
            item.items.some((item) => typeof item === 'string' && /Location\d+Address/.test(item)),
        )
      return (
        items.length > 0 && (
          <div class={this.getSectionClasses(section.meta.composition, depth)}>
            {section.ui?.title && this.renderSectionTitle(section.ui.title, depth)}
            {section.ui?.description && this.renderSectionDescription(section.ui.description)}
            {useQuestionGroup ? this.renderQuestionGroup(section.items, depth) : items}
          </div>
        )
      )
    })
  }

  render(): JSX.Element {
    return this.isQuestionnaireValid() ? (
      <form class="questionnaire">{this.renderSections(this.sections)}</form>
    ) : (
      <p class="message">Empty questionnaire. Please, set questions and sections.</p>
    )
  }
}
